import type { FeatureModule, FeatureState } from '../types';

interface ReadingGuideOptions {
  height: number;
  opacity: number;
}

const DEFAULT_OPTIONS: ReadingGuideOptions = {
  height: 8,
  opacity: 0.3,
};

export default function createReadingGuideModule(): FeatureModule {
  let enabled = false;
  let guideEl: HTMLDivElement | null = null;
  let currentY = 0;
  let options: ReadingGuideOptions = { ...DEFAULT_OPTIONS };
  // If the host page strips the guide element from the DOM (some themes
  // run a clean-up MutationObserver of their own), we re-mount it on the
  // next tick. The observer is wired up in activate() and torn down in
  // deactivate().
  let remountObserver: MutationObserver | null = null;
  const STORAGE_KEY = 'accessify-reading-guide';
  const GUIDE_ID = 'accessify-reading-guide';
  // The guide lives in the host light-DOM (it has to overlay the host
  // page, and `position: fixed` from inside the widget shadow root would
  // be just as exposed to host CSS). We tag it with both an id and a
  // class so external CSS hooks can target it without fighting id
  // specificity — R6-10's request.
  const GUIDE_CLASS = 'accessify-reading-guide accessify-overlay';

  function handleMouseMove(e: MouseEvent) {
    currentY = e.clientY;
    updateGuidePosition();
  }

  // Touch: a continuously-following guide fought the page scroll — you could
  // not position it without scrolling, so it felt broken. Instead we let the
  // page scroll normally and reposition the guide only on a deliberate TAP.
  // A touch that moves past a small threshold is a scroll, not a tap.
  let touchStartX = 0;
  let touchStartY = 0;
  let touchMoved = false;
  function handleTouchStart(e: TouchEvent) {
    if (e.touches.length !== 1) { touchMoved = true; return; }
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    touchMoved = false;
  }
  function handleTouchMove(e: TouchEvent) {
    if (e.touches.length !== 1) return;
    if (Math.abs(e.touches[0].clientX - touchStartX) > 10 || Math.abs(e.touches[0].clientY - touchStartY) > 10) {
      touchMoved = true; // scroll, not a tap — leave the guide where it is
    }
  }
  function handleTouchEnd(e: TouchEvent) {
    if (touchMoved || !enabled) return;
    const t = e.changedTouches[0];
    if (!t) return;
    currentY = t.clientY;
    updateGuidePosition();
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (!enabled || !guideEl) return;
    const STEP = 10;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      currentY = Math.max(0, currentY - STEP);
      updateGuidePosition();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      currentY = Math.min(window.innerHeight, currentY + STEP);
      updateGuidePosition();
    }
  }

  function updateGuidePosition() {
    if (!guideEl) return;
    const halfHeight = options.height / 2;
    guideEl.style.setProperty('top', `${currentY - halfHeight}px`, 'important');
  }

  function createGuideElement(): HTMLDivElement {
    const el = document.createElement('div');
    el.id = GUIDE_ID;
    el.className = GUIDE_CLASS;
    el.setAttribute('role', 'presentation');
    el.setAttribute('aria-hidden', 'true');
    // Use setProperty('...', '!important') instead of Object.assign so
    // the inline styles survive hostile host CSS like
    // `* { z-index: auto !important; }` — R6-7's worry case.
    el.style.setProperty('position', 'fixed', 'important');
    el.style.setProperty('left', '0', 'important');
    el.style.setProperty('width', '100%', 'important');
    el.style.setProperty('height', `${options.height}px`, 'important');
    el.style.setProperty('background-color', `rgba(255, 255, 0, ${options.opacity})`, 'important');
    el.style.setProperty('pointer-events', 'none', 'important');
    el.style.setProperty('z-index', '2147483646', 'important');
    el.style.setProperty('top', '50%', 'important');
    el.style.setProperty('transition', 'top 0.05s linear', 'important');
    el.style.setProperty('box-shadow', '0 0 4px rgba(0,0,0,0.2)', 'important');
    return el;
  }

  /**
   * Watch document.documentElement for child removals. If the host page
   * tears down the guide (some Single-Page-Apps run cleanup observers on
   * unknown DOM children), we re-mount it on the next animation frame.
   * Only active while the feature is enabled — disconnected in deactivate().
   */
  function startRemountObserver() {
    if (remountObserver || !guideEl) return;
    remountObserver = new MutationObserver(() => {
      if (!enabled || !guideEl) return;
      if (!guideEl.isConnected) {
        // Re-attach the same element (it still holds its inline styles).
        document.documentElement.appendChild(guideEl);
      }
    });
    remountObserver.observe(document.documentElement, { childList: true, subtree: false });
  }

  function stopRemountObserver() {
    remountObserver?.disconnect();
    remountObserver = null;
  }

  function activate() {
    enabled = true;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        options = {
          height: Math.min(60, Math.max(2, parsed.height ?? DEFAULT_OPTIONS.height)),
          opacity: Math.min(1, Math.max(0.1, parsed.opacity ?? DEFAULT_OPTIONS.opacity)),
        };
      } catch {
        options = { ...DEFAULT_OPTIONS };
      }
    }

    guideEl = createGuideElement();
    document.documentElement.appendChild(guideEl);
    startRemountObserver();

    currentY = window.innerHeight / 2;
    updateGuidePosition();

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('keydown', handleKeyDown);
  }

  function deactivate() {
    enabled = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
    document.removeEventListener('keydown', handleKeyDown);
    stopRemountObserver();

    if (guideEl) {
      guideEl.remove();
      guideEl = null;
    }
    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'reading-guide',
    name: () => 'Reading Guide',
    description: 'Horizontal ruler that follows your cursor for easier reading',
    icon: 'reading-guide',
    category: 'cognitive',
    activate,
    deactivate,
    getState: (): FeatureState => ({
      id: 'reading-guide',
      enabled,
      value: { ...options },
    }),
    setState: (newOptions: Partial<ReadingGuideOptions>) => {
      options = {
        height: Math.min(60, Math.max(2, newOptions.height ?? options.height)),
        opacity: Math.min(1, Math.max(0.1, newOptions.opacity ?? options.opacity)),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(options));

      if (guideEl) {
        guideEl.style.setProperty('height', `${options.height}px`, 'important');
        guideEl.style.setProperty('background-color', `rgba(255, 255, 0, ${options.opacity})`, 'important');
        updateGuidePosition();
      }
    },
  };
}
